'use client';

import type { UseFormReturn } from 'react-hook-form';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@kit/ui/card';
import { DateTimeField } from '@kit/ui/date-time-field';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { SettingToggleRow } from '@kit/ui/setting-toggle-row';
import { Trans } from '@kit/ui/trans';

import type { UpdateAuditionInput } from '../../schemas/audition.schema';

interface CompetitionNetworkingTabProps {
  form: UseFormReturn<UpdateAuditionInput>;
}

/** Format a Date/string value for a `datetime-local` input. */
function toDatetimeLocal(value: Date | string | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  // Local wall-clock `YYYY-MM-DDTHH:mm` (Critical Rule 19 — `toISOString()`
  // would render the value in UTC and shift the displayed time by the offset).
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/**
 * Networking & Features tab — competition-only.
 *
 * Migrated from the deleted standalone Competition Settings form (networking +
 * features sections). Edits the same `UpdateAuditionInput` over the same
 * `updateAuditionAction` write path as every other tab (Critical Rule #8). The
 * networking sub-fields stay gated behind the `isNetworkingEvent` switch via
 * `form.watch`, exactly as the previous surface did.
 */
export function CompetitionNetworkingTab({
  form,
}: CompetitionNetworkingTabProps) {
  const isNetworking = form.watch('isNetworkingEvent');

  return (
    <div className="space-y-6">
      {/* Networking & selections */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans i18nKey="auditions:editor.competitionNetworking.networking.title" />
          </CardTitle>
          <CardDescription>
            <Trans i18nKey="auditions:editor.competitionNetworking.networking.description" />
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={form.control}
            name="isNetworkingEvent"
            render={({ field }) => (
              <FormItem>
                <SettingToggleRow
                  label={
                    <Trans i18nKey="auditions:editor.competitionNetworking.fields.enable.label" />
                  }
                  description={
                    <Trans i18nKey="auditions:editor.competitionNetworking.fields.enable.description" />
                  }
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                />
              </FormItem>
            )}
          />

          {isNetworking && (
            <>
              <div className="divide-y">
                <FormField
                  control={form.control}
                  name="networkingAllowOrgSelection"
                  render={({ field }) => (
                    <FormItem>
                      <SettingToggleRow
                        label={
                          <Trans i18nKey="auditions:editor.competitionNetworking.fields.allowOrgSelection.label" />
                        }
                        description={
                          <Trans i18nKey="auditions:editor.competitionNetworking.fields.allowOrgSelection.description" />
                        }
                        checked={field.value ?? false}
                        onCheckedChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="networkingAllowIndividualSelection"
                  render={({ field }) => (
                    <FormItem>
                      <SettingToggleRow
                        label={
                          <Trans i18nKey="auditions:editor.competitionNetworking.fields.allowIndividualSelection.label" />
                        }
                        description={
                          <Trans i18nKey="auditions:editor.competitionNetworking.fields.allowIndividualSelection.description" />
                        }
                        checked={field.value ?? false}
                        onCheckedChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4 rounded-lg border p-4">
                <div className="text-sm font-medium">
                  <Trans i18nKey="auditions:editor.competitionNetworking.selectionPeriods.title" />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="networkingOrgSelectionStart"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          <Trans i18nKey="auditions:editor.competitionNetworking.fields.orgSelectionStart.label" />
                        </FormLabel>
                        <FormControl>
                          <DateTimeField
                            value={toDatetimeLocal(field.value)}
                            onChange={(next) =>
                              field.onChange(next ? new Date(next) : null)
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="networkingOrgSelectionEnd"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          <Trans i18nKey="auditions:editor.competitionNetworking.fields.orgSelectionEnd.label" />
                        </FormLabel>
                        <FormControl>
                          <DateTimeField
                            value={toDatetimeLocal(field.value)}
                            onChange={(next) =>
                              field.onChange(next ? new Date(next) : null)
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="networkingIndividualSelectionStart"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          <Trans i18nKey="auditions:editor.competitionNetworking.fields.individualSelectionStart.label" />
                        </FormLabel>
                        <FormControl>
                          <DateTimeField
                            value={toDatetimeLocal(field.value)}
                            onChange={(next) =>
                              field.onChange(next ? new Date(next) : null)
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="networkingIndividualSelectionEnd"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          <Trans i18nKey="auditions:editor.competitionNetworking.fields.individualSelectionEnd.label" />
                        </FormLabel>
                        <FormControl>
                          <DateTimeField
                            value={toDatetimeLocal(field.value)}
                            onChange={(next) =>
                              field.onChange(next ? new Date(next) : null)
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="divide-y">
                <FormField
                  control={form.control}
                  name="networkingShowMatchesToOrgs"
                  render={({ field }) => (
                    <FormItem>
                      <SettingToggleRow
                        label={
                          <Trans i18nKey="auditions:editor.competitionNetworking.fields.showMatchesToOrgs.label" />
                        }
                        description={
                          <Trans i18nKey="auditions:editor.competitionNetworking.fields.showMatchesToOrgs.description" />
                        }
                        checked={field.value ?? false}
                        onCheckedChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="networkingShowMatchesToIndividuals"
                  render={({ field }) => (
                    <FormItem>
                      <SettingToggleRow
                        label={
                          <Trans i18nKey="auditions:editor.competitionNetworking.fields.showMatchesToIndividuals.label" />
                        }
                        description={
                          <Trans i18nKey="auditions:editor.competitionNetworking.fields.showMatchesToIndividuals.description" />
                        }
                        checked={field.value ?? false}
                        onCheckedChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Features */}
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans i18nKey="auditions:editor.competitionNetworking.features.title" />
          </CardTitle>
          <CardDescription>
            <Trans i18nKey="auditions:editor.competitionNetworking.features.description" />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="divide-y">
            <FormField
              control={form.control}
              name="hasFinalShow"
              render={({ field }) => (
                <FormItem>
                  <SettingToggleRow
                    label={
                      <Trans i18nKey="auditions:editor.competitionNetworking.fields.finalShow.label" />
                    }
                    description={
                      <Trans i18nKey="auditions:editor.competitionNetworking.fields.finalShow.description" />
                    }
                    checked={field.value ?? false}
                    onCheckedChange={field.onChange}
                  />
                </FormItem>
              )}
            />

            {/* BAL-408: hide the "My Meetings" tab in the partner portal. */}
            <FormField
              control={form.control}
              name="showPartnerMeetings"
              render={({ field }) => (
                <FormItem>
                  <SettingToggleRow
                    label={
                      <Trans i18nKey="auditions:editor.competitionNetworking.fields.showPartnerMeetings.label" />
                    }
                    description={
                      <Trans i18nKey="auditions:editor.competitionNetworking.fields.showPartnerMeetings.description" />
                    }
                    checked={field.value ?? true}
                    onCheckedChange={field.onChange}
                  />
                </FormItem>
              )}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
